import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowsClockwise, Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { PageHeader } from '@/components/core';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useCurrentOrganizationName } from '@/hooks/use-organizations';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createDepartmentTableColumns } from '../../components/department-table-columns/department-table-columns';
import { DepartmentFormDialog } from '../../components/department-form-dialog/department-form-dialog';
import {
  useDeleteDepartment,
  useGetDepartments,
  useSyncDepartmentRoles,
} from '../../hooks/use-departments';
import { DEFAULT_DEPARTMENTS_ORDER } from '../../graphql/queries';
import { Department, DepartmentsPagingResult } from '../../types/department.types';

export const DepartmentManagerPage = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SETTINGS.DEPARTMENTS_CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SETTINGS.DEPARTMENTS_UPDATE);
  const canDelete = useHasPermission(HRM_PERMISSIONS.SETTINGS.DEPARTMENTS_DELETE);

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_DEPARTMENTS_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; department: Department | null }>({
    open: false,
    department: null,
  });
  const [deleteState, setDeleteState] = useState<{ open: boolean; department: Department | null }>({
    open: false,
    department: null,
  });

  const { data, isLoading, error } = useGetDepartments({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { Departments?: DepartmentsPagingResult })?.Departments;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteDepartment, isPending: isDeleting } = useDeleteDepartment();
  const { mutate: syncRoles, isPending: isSyncing } = useSyncDepartmentRoles();
  const { data: organizationName } = useCurrentOrganizationName();

  const handleDeleteConfirm = async () => {
    if (!deleteState.department) return;
    await deleteDepartment(deleteState.department);
    setDeleteState({ open: false, department: null });
  };

  const columns = createDepartmentTableColumns({
    t,
    onEdit: (department) => setFormState({ open: true, department }),
    onDelete: (department) => setDeleteState({ open: true, department }),
    canUpdate,
    canDelete,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('DEPARTMENTS')}
        actions={
          canCreate
            ? [
                {
                  label: isSyncing ? t('SYNCING_ROLES') : t('SYNC_ROLES'),
                  icon: <ArrowsClockwise className={`size-5 ${isSyncing ? 'animate-spin' : ''}`} />,
                  variant: 'outline' as const,
                  disabled: isSyncing || !organizationName,
                  onClick: () => organizationName && syncRoles(organizationName),
                },
                {
                  label: t('CREATE_DEPARTMENT'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setFormState({ open: true, department: null }),
                },
              ]
            : []
        }
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <DepartmentFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, department: open ? prev.department : null }))
        }
        department={formState.department}
      />

      <ConfirmationModal
        open={deleteState.open}
        onOpenChange={(open) =>
          setDeleteState({ open, department: open ? deleteState.department : null })
        }
        title={t('DELETE_DEPARTMENT')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
